import { atom, useAtomValue } from 'jotai'
import { GlobalState } from '@highlight/ipc'
import type { AudioNoteState } from '@shared/types/AudioNoteState'

/**
 * How many renderer-initiated recording starts are still pending: main is
 * working on the start, or it has begun the session and this renderer's
 * audio-note-state replica hasn't shown it yet. While this is above zero a
 * session may still be about to appear; once it drops to zero with no
 * session, none is coming.
 *
 * A count, not a flag: a double-click lands two starts, and the second
 * settles as soon as the session shows, which must not read as the first
 * having settled.
 */
export const audioNoteStartsInFlightAtom = atom(0)

const startPendingAtom = atom((get) => get(audioNoteStartsInFlightAtom) > 0)

export const useIsAudioNoteStartPending = () => useAtomValue(startPendingAtom)

/** Main has said the session began; a broadcast that hasn't arrived by now is not coming. */
const SESSION_ARRIVAL_TIMEOUT_MS = 5000

const hasSession = (state: AudioNoteState | undefined) => !!state && state.status !== 'idle' && state.status !== 'saved'

/**
 * Resolve once the audio-note-state replica shows a session. Main reports a
 * started session over the IPC reply while the state itself arrives on the
 * GlobalState broadcast, and nothing orders the two — so a start that took
 * stays pending until the state that proves it has landed here, rather than
 * being dropped the moment the reply does.
 */
export function waitForAudioNoteSession(): Promise<void> {
  if (hasSession(GlobalState.get<AudioNoteState>('audio-note-state'))) return Promise.resolve()
  return new Promise((resolve) => {
    const settle = () => {
      clearTimeout(timer)
      GlobalState.off('audio-note-state', onState)
      resolve()
    }
    const onState = (state: AudioNoteState | undefined) => {
      if (hasSession(state)) settle()
    }
    const timer = setTimeout(settle, SESSION_ARRIVAL_TIMEOUT_MS)
    GlobalState.on('audio-note-state', onState)
  })
}
